if (!customElements.get('card-stack')) {
  /**
   * Scroll-driven card stack: something pins in place the moment its
   * center reaches the viewport's center, staying there while each card
   * slides up from below — starting past the bottom of the viewport — to
   * cover the previous one. Once the cards run out, it releases and
   * scrolling continues normally. No GSAP, no scroll-jacking.
   *
   * What pins (and what it's centered on) differs by breakpoint:
   * - Desktop (image beside the text): the whole row pins, centered on the
   *   image, since the image sits right next to the cards.
   * - Mobile (image stacked above the text, cards pushed below both): only
   *   the card box itself pins, centered on itself — a regular
   *   pinned-card-scroll. Pinning the whole row there would center on the
   *   image again, which (being taller than half the viewport) pushes the
   *   pin position to the very top, showing the image at the top of the
   *   screen with the card wherever it happens to fall beneath it — not
   *   centered at all.
   *
   * `<card-stack>` (this element) itself never moves — it just reserves the
   * scroll height. Its own `getBoundingClientRect().top` is therefore
   * always a reliable, continuous measure of scroll position, even while
   * the pin target itself is `position: fixed` and no longer reports a
   * real scroll-linked position. `pinTargetOffset` (measured once,
   * unpinned) is the constant gap between this element's top and the pin
   * target's natural top — on desktop that's ~0 (the pin target *is* the
   * row); on mobile it's the height of the image + heading/text sitting
   * above the card box within that row.
   *
   * The pin is done manually with `position: fixed` (toggled in JS), not
   * `position: sticky` — this section sits inside several ancestors
   * (image-with-text wrappers, page containers) and any one of them
   * getting `overflow` or a `transform` later would silently break sticky.
   *
   * Without JS (or with prefers-reduced-motion), everything stays
   * `position: static` and the cards render as a plain stacked list — see
   * section-media-and-text-cards.css.
   */
  class CardStack extends HTMLElement {
    connectedCallback() {
      this.pin = this.querySelector('.card-stack__pin');
      this.media = this.querySelector('.media-cards__media');
      this.cardsBox = this.querySelector('.card-stack__sticky');
      this.cards = Array.from(this.querySelectorAll('.card-stack__card'));
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.desktopQuery = window.matchMedia('(min-width: 768px)');

      if (!this.pin || !this.media || !this.cardsBox || this.cards.length < 2 || this.reducedMotion) return;

      this.stepHeight = parseFloat(getComputedStyle(this).getPropertyValue('--card-stack-step-height')) || 380;
      this.distance = 0;
      this.pinOffset = 0;
      this.pinTargetOffset = 0;
      this.pinTarget = null; // whichever of `.pin` / `.cardsBox` is pinning
      this.travel = 0; // how far below the viewport an incoming card starts
      this.phase = null; // 'before' | 'pinned' | 'after'
      this.rafId = null;
      this.lastWidth = window.innerWidth;

      this.measure = this.measure.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.classList.add('card-stack--active');
      this.measure();

      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    onResize() {
      // Mobile browsers fire `resize` when the address bar shows/hides while
      // scrolling, changing innerHeight only. Re-measuring then would resize
      // this element's reserved scroll height while the visitor is scrolled
      // deep inside it, snapping the pinned content to a new position.
      if (window.innerWidth === this.lastWidth) return;
      this.lastWidth = window.innerWidth;
      this.measure();
    }

    resetTarget(el) {
      el.style.position = '';
      el.style.top = '';
      el.style.left = '';
      el.style.width = '';
    }

    measure() {
      // Clear both — a resize can cross the breakpoint and switch which one
      // is the pin target, leaving the other with stale inline styles.
      this.resetTarget(this.pin);
      this.resetTarget(this.cardsBox);

      const isDesktop = this.desktopQuery.matches;
      this.pinTarget = isDesktop ? this.pin : this.cardsBox;
      // The thing we're centering is always the pin target's own first bit
      // of content — the image on desktop (the row's first child), the
      // card box on mobile (centered on itself) — so its height is simply
      // the pin target's own natural height in the image case, or the
      // card box's natural height in the mobile case.
      const anchorHeight = isDesktop
        ? this.media.getBoundingClientRect().height
        : this.cardsBox.getBoundingClientRect().height;

      this.distance = Math.max(0, (this.cards.length - 1) * this.stepHeight);
      this.pinOffset = Math.max(0, (window.innerHeight - anchorHeight) / 2);

      // Everything is unpinned right now (both targets were just reset), so
      // this is the one moment we can measure the pin target's natural
      // offset from `<card-stack>`'s own top.
      const outerRect = this.getBoundingClientRect();
      this.pinTargetOffset = this.pinTarget.getBoundingClientRect().top - outerRect.top;

      this.pinHeight = this.pin.getBoundingClientRect().height;
      this.style.height = this.pinHeight + this.distance + 'px';

      // Incoming cards should fly in from the bottom of the *screen*, not
      // just from directly under the previous card.
      this.travel = Math.max(window.innerHeight, this.cardsBox.getBoundingClientRect().height);
      this.phase = null; // force update() to re-apply the correct phase below
      this.update();
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    setPhase(phase, rect) {
      if (this.phase === phase && phase !== 'pinned') return;
      this.phase = phase;
      const target = this.pinTarget;

      if (phase === 'before') {
        // Clear back to '', not a hardcoded 'static': `.card-stack__pin`'s
        // CSS default is `static` (fine, no positioned children), but
        // `.card-stack__sticky`'s is `relative` — it needs to stay a
        // positioned ancestor for `.card-stack__card`'s
        // `position: absolute; inset: 0` on mobile, where it's the pin
        // target. Forcing `static` here breaks that and the card balloons
        // to size against the much taller outer `<card-stack>` instead.
        target.style.position = '';
        target.style.top = '';
        target.style.left = '';
        target.style.width = '';
      } else if (phase === 'pinned') {
        target.style.position = 'fixed';
        target.style.top = this.pinOffset + 'px';
        target.style.left = rect.left + 'px';
        target.style.width = rect.width + 'px';
      } else {
        // 'after': release at the bottom of the reserved space so it
        // scrolls away with the rest of the page instead of staying fixed
        // forever.
        target.style.position = 'absolute';
        target.style.top = this.distance + 'px';
        target.style.left = '0';
        target.style.width = '100%';
      }
    }

    update() {
      if (this.distance <= 0) return;
      // `this` (the outer element) never gets repositioned, so its rect is
      // always a true, continuous read of scroll position — unlike the pin
      // target itself once it's `position: fixed`.
      const outerRect = this.getBoundingClientRect();
      const effectiveTop = outerRect.top + this.pinTargetOffset;
      const scrolled = Math.min(this.distance, Math.max(0, this.pinOffset - effectiveTop));

      if (effectiveTop > this.pinOffset) {
        this.setPhase('before');
      } else if (scrolled >= this.distance) {
        this.setPhase('after');
      } else {
        // Read the pin target's own rect for left/width right before (or
        // while) pinning it — while still unpinned this is its natural
        // position; while already pinned it's just its current (stable)
        // fixed box, which is a no-op to reapply.
        this.setPhase('pinned', this.pinTarget.getBoundingClientRect());
      }

      this.cards.forEach((card, i) => {
        if (i === 0) return; // first card starts already in place
        const start = (i - 1) * this.stepHeight;
        const progress = Math.min(1, Math.max(0, (scrolled - start) / this.stepHeight));
        card.style.transform = 'translateY(' + (1 - progress) * this.travel + 'px)';
      });
    }
  }

  customElements.define('card-stack', CardStack);
}
